import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/ApiService';

type Row = {
  uniqueid: string; start: string; src: string; dst: string; ext: string | null; extName: string | null;
  disposition: string; duration: number; billsec: number; direction: string; missed: boolean; answered: boolean;
  tenantId: number | null; tenantName: string | null;
};
type Summary = { all: number; inbound: number; outbound: number; internal: number; missed: number; answered: number; billsecTotal: number };
type Resp = { summary: Summary; total: number; page: number; limit: number; rows: Row[]; isSuper: boolean; tenants: { id: number; name: string }[] };

function fmtDur(s: number) {
  if (!s) return '0:00';
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), ss = s % 60;
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(ss).padStart(2, '0')}` : `${m}:${String(ss).padStart(2, '0')}`;
}
function ymd(d: Date) { return d.toISOString().slice(0, 10); }

const TYPES = [
  { v: 'all', label: 'All calls' },
  { v: 'inbound', label: 'Inbound' },
  { v: 'outbound', label: 'Outbound' },
  { v: 'missed', label: 'Missed' },
  { v: 'internal', label: 'Internal' },
];

export default function CdrPage() {
  const today = ymd(new Date());
  const monthAgo = ymd(new Date(Date.now() - 30 * 864e5));
  const [from, setFrom] = useState(monthAgo);
  const [to, setTo] = useState(today);
  const [type, setType] = useState('all');
  const [tenantId, setTenantId] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(100);
  const [data, setData] = useState<Resp | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.getCdr({ from, to, type, tenant_id: tenantId || undefined, page, limit });
      setData(d); setErr('');
    } catch (e: any) { setErr(e?.response?.data?.error || 'Failed to load call records'); }
    finally { setLoading(false); }
  }, [from, to, type, tenantId, page, limit]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [from, to, type, tenantId]);

  const exportCsv = async () => {
    const d: Resp = await api.getCdr({ from, to, type, tenant_id: tenantId || undefined, page: 1, limit: 100000 });
    const head = ['Time', 'Tenant', 'Direction', 'From', 'To', 'Extension', 'Status', 'Duration(s)', 'TalkTime(s)'];
    const esc = (v: any) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
    const lines = [head.join(',')].concat(
      d.rows.map((r) => [r.start, r.tenantName || r.tenantId || '', r.direction, r.src, r.dst,
        r.ext ? `${r.ext}${r.extName ? ' ' + r.extName : ''}` : '', r.missed ? 'MISSED' : r.disposition,
        r.duration, r.billsec].map(esc).join(','))
    );
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `cdr_${from}_to_${to}_${type}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const s = data?.summary;
  const isSuper = data?.isSuper;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;

  return (
    <>
      <div className="admin-page-title">Call Records (CDR)</div>
      <div className="admin-page-subtitle">
        Call detail records from the phone system. {isSuper ? 'Showing all tenants — filter by tenant below.' : 'Showing your organisation.'} Use the date and type filters, or export to CSV for billing.
      </div>

      <div className="content-card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label className="admin-field"><span>From</span><input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} /></label>
          <label className="admin-field"><span>To</span><input type="date" value={to} min={from} max={today} onChange={(e) => setTo(e.target.value)} /></label>
          <label className="admin-field"><span>Call type</span>
            <select value={type} onChange={(e) => setType(e.target.value)}>{TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}</select>
          </label>
          {isSuper && (
            <label className="admin-field"><span>Tenant</span>
              <select value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
                <option value="">All tenants</option>
                {(data?.tenants || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </label>
          )}
          <button className="secondary-btn" onClick={load}>Apply</button>
          <button className="secondary-btn" onClick={exportCsv}>Export CSV</button>
        </div>
      </div>

      <div className="dashboard-grid">
        <StatCard title="Total calls" value={s?.all ?? 0} />
        <StatCard title="Inbound" value={s?.inbound ?? 0} />
        <StatCard title="Outbound" value={s?.outbound ?? 0} />
        <StatCard title="Missed" value={s?.missed ?? 0} danger={(s?.missed ?? 0) > 0} />
      </div>

      <div className="content-card">
        <div className="admin-card-header">
          <h3>Records{data ? `  ·  ${data.total} calls  ·  talk time ${fmtDur(s?.billsecTotal ?? 0)}` : ''}</h3>
        </div>
        {err && <p className="admin-muted" style={{ color: '#dc2626' }}>{err}</p>}
        {loading && !data && <p className="admin-muted">Loading…</p>}
        {data && data.rows.length === 0 && <p className="admin-muted">No calls match these filters.</p>}
        {data && data.rows.length > 0 && (
          <>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Time</th>{isSuper && <th>Tenant</th>}<th>Direction</th><th>From</th><th>To</th><th>Extension</th><th>Status</th><th>Duration</th><th>Talk time</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.uniqueid} style={r.missed ? { background: '#fef2f2' } : undefined}>
                    <td className="admin-muted" style={{ whiteSpace: 'nowrap' }}>{r.start}</td>
                    {isSuper && <td>{r.tenantName || (r.tenantId != null ? `Tenant ${r.tenantId}` : <span className="admin-muted">—</span>)}</td>}
                    <td><span className={`admin-pill ${r.direction === 'outbound' ? 'green' : r.direction === 'inbound' ? '' : ''}`}>{r.direction}</span></td>
                    <td>{r.src || '—'}</td>
                    <td>{r.dst || '—'}</td>
                    <td>{r.ext ? <><strong>{r.ext}</strong>{r.extName ? <span className="admin-muted"> {r.extName}</span> : null}</> : <span className="admin-muted">—</span>}</td>
                    <td>{r.missed ? <span className="admin-pill red">Missed</span> : <span className="admin-pill green">{r.disposition || 'OK'}</span>}</td>
                    <td>{fmtDur(r.duration)}</td>
                    <td>{fmtDur(r.billsec)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 12 }}>
              <button className="secondary-btn" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
              <span className="admin-muted">Page {data.page} of {totalPages}</span>
              <button className="secondary-btn" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function StatCard({ title, value, danger }: { title: string; value: number; danger?: boolean }) {
  return (
    <div className="stat-card">
      <div className="stat-title">{title}</div>
      <div className="stat-value" style={danger ? { color: '#dc2626' } : undefined}>{value}</div>
    </div>
  );
}
